/**
 * ClassificationPanel — Staff Classification Workspace (Phase 2C refactor)
 *
 * Architecture:
 * - Taxonomy vocabulary is loaded exclusively from GET /api/document-taxonomy/published.
 *   No hard-coded CLASS_BY_CATEGORY mirror. Adding a new published class automatically
 *   appears in this selector without any React code change.
 * - Staff confirmation is AUTHORITATIVE and never overwritten by AI.
 * - AI suggestion is preserved as a non-authoritative record shown below confirmed.
 * - Show current classification prominently; editing only after "Change Classification".
 * - Search supports: label, canonical code, aliases, searchable_terms, ui_path breadcrumb.
 * - Browse hierarchy is dynamically derived from ui_path; no hard-coded groups.
 * - State/jurisdiction selector required for all US state return classes.
 * - Confidence displayed only for AI-generated classifications.
 * - Loading/error/retry states are surfaced; staff cannot confirm if taxonomy fails to load.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";

// US State classes that require a state/jurisdiction selector
const STATE_CLASSES = new Set([
  "us_state_individual_return",
  "us_state_business_return",
  "us_state_amended_return",
]);

// U.S. States + DC + territories (canonical IETF code → label)
const US_STATE_OPTIONS = [
  { code: "US-AL", label: "Alabama" },        { code: "US-AK", label: "Alaska" },
  { code: "US-AZ", label: "Arizona" },        { code: "US-AR", label: "Arkansas" },
  { code: "US-CA", label: "California" },     { code: "US-CO", label: "Colorado" },
  { code: "US-CT", label: "Connecticut" },    { code: "US-DE", label: "Delaware" },
  { code: "US-FL", label: "Florida" },        { code: "US-GA", label: "Georgia" },
  { code: "US-HI", label: "Hawaii" },         { code: "US-ID", label: "Idaho" },
  { code: "US-IL", label: "Illinois" },       { code: "US-IN", label: "Indiana" },
  { code: "US-IA", label: "Iowa" },           { code: "US-KS", label: "Kansas" },
  { code: "US-KY", label: "Kentucky" },       { code: "US-LA", label: "Louisiana" },
  { code: "US-ME", label: "Maine" },          { code: "US-MD", label: "Maryland" },
  { code: "US-MA", label: "Massachusetts" },  { code: "US-MI", label: "Michigan" },
  { code: "US-MN", label: "Minnesota" },      { code: "US-MS", label: "Mississippi" },
  { code: "US-MO", label: "Missouri" },       { code: "US-MT", label: "Montana" },
  { code: "US-NE", label: "Nebraska" },       { code: "US-NV", label: "Nevada" },
  { code: "US-NH", label: "New Hampshire" },  { code: "US-NJ", label: "New Jersey" },
  { code: "US-NM", label: "New Mexico" },     { code: "US-NY", label: "New York" },
  { code: "US-NC", label: "North Carolina" }, { code: "US-ND", label: "North Dakota" },
  { code: "US-OH", label: "Ohio" },           { code: "US-OK", label: "Oklahoma" },
  { code: "US-OR", label: "Oregon" },         { code: "US-PA", label: "Pennsylvania" },
  { code: "US-RI", label: "Rhode Island" },   { code: "US-SC", label: "South Carolina" },
  { code: "US-SD", label: "South Dakota" },   { code: "US-TN", label: "Tennessee" },
  { code: "US-TX", label: "Texas" },          { code: "US-UT", label: "Utah" },
  { code: "US-VT", label: "Vermont" },        { code: "US-VA", label: "Virginia" },
  { code: "US-WA", label: "Washington" },     { code: "US-WV", label: "West Virginia" },
  { code: "US-WI", label: "Wisconsin" },      { code: "US-WY", label: "Wyoming" },
  { code: "US-DC", label: "District of Columbia" },
  { code: "US-PR", label: "Puerto Rico" },
  { code: "US-VI", label: "U.S. Virgin Islands" },
  { code: "US-GU", label: "Guam" },
  { code: "US-AS", label: "American Samoa" },
  { code: "US-MP", label: "Northern Mariana Islands" },
];

const BAND_COLOR = {
  very_high: "text-emerald-700 bg-emerald-50",
  high:      "text-emerald-700 bg-emerald-50",
  medium:    "text-amber-700 bg-amber-50",
  low:       "text-slate-500 bg-slate-100",
  unknown:   "text-slate-400 bg-slate-50",
};

const BAND_LABEL = {
  very_high: "Very High", high: "High", medium: "Medium", low: "Low", unknown: "Unknown",
};

const chip = (s) => (s || "").replace(/_/g, " ");

const JX_DISPLAY = {
  us: "United States", US: "United States",
  uk: "United Kingdom", UK: "United Kingdom",
  th: "Thailand",
  "US-AL": "Alabama", "US-AK": "Alaska", "US-AZ": "Arizona", "US-AR": "Arkansas",
  "US-CA": "California", "US-CO": "Colorado", "US-CT": "Connecticut", "US-DE": "Delaware",
  "US-FL": "Florida", "US-GA": "Georgia", "US-HI": "Hawaii", "US-ID": "Idaho",
  "US-IL": "Illinois", "US-IN": "Indiana", "US-IA": "Iowa", "US-KS": "Kansas",
  "US-KY": "Kentucky", "US-LA": "Louisiana", "US-ME": "Maine", "US-MD": "Maryland",
  "US-MA": "Massachusetts", "US-MI": "Michigan", "US-MN": "Minnesota", "US-MS": "Mississippi",
  "US-MO": "Missouri", "US-MT": "Montana", "US-NE": "Nebraska", "US-NV": "Nevada",
  "US-NH": "New Hampshire", "US-NJ": "New Jersey", "US-NM": "New Mexico", "US-NY": "New York",
  "US-NC": "North Carolina", "US-ND": "North Dakota", "US-OH": "Ohio", "US-OK": "Oklahoma",
  "US-OR": "Oregon", "US-PA": "Pennsylvania", "US-RI": "Rhode Island", "US-SC": "South Carolina",
  "US-SD": "South Dakota", "US-TN": "Tennessee", "US-TX": "Texas", "US-UT": "Utah",
  "US-VT": "Vermont", "US-VA": "Virginia", "US-WA": "Washington", "US-WV": "West Virginia",
  "US-WI": "Wisconsin", "US-WY": "Wyoming", "US-DC": "District of Columbia",
  "US-PR": "Puerto Rico", "US-VI": "U.S. Virgin Islands", "US-GU": "Guam",
  "US-AS": "American Samoa", "US-MP": "Northern Mariana Islands",
};
const jxName = (code) => JX_DISPLAY[code] || chip(code);

// Normalize a string for search comparison: lowercase, strip punctuation/spaces.
// Handles: "1099-R" → "1099r", "W-2G" → "w2g", "PND 90" → "pnd90"
function normalizeSearch(s) {
  return (s || "").toLowerCase().replace(/[-\s.,()]/g, "");
}

// Build a hierarchical grouping from ui_path.
// Returns: [ { topLabel, subgroups: [ { subLabel, classes: [...] } ] } ]
function buildHierarchy(classes) {
  const topOrder = [];
  const topMap = {};
  for (const cls of classes) {
    if (!cls.active && cls.active !== undefined) continue;
    const path = cls.ui_path || [];
    const top = path[0] || "Other";
    const sub = path.length >= 3 ? path.slice(1).join(" → ") : (path[1] || "");
    if (!topMap[top]) {
      topOrder.push(top);
      topMap[top] = { topLabel: top, subMap: {}, subOrder: [] };
    }
    const g = topMap[top];
    if (!g.subMap[sub]) {
      g.subOrder.push(sub);
      g.subMap[sub] = [];
    }
    g.subMap[sub].push(cls);
  }
  return topOrder.map((top) => ({
    topLabel: top,
    subgroups: topMap[top].subOrder.map((sub) => ({
      subLabel: sub,
      classes: topMap[top].subMap[sub],
    })),
  }));
}

export function ClassificationPanel({
  docId, onRefresh,
  category, documentType, jurisdictions, formNumber, filingAuthority,
}) {
  const [rec, setRec]         = useState(null);
  const [aiReady, setAiReady] = useState(false);
  const [cats, setCats]       = useState([]);
  const [mode, setMode]       = useState("idle"); // idle | suggesting | editing | saving

  // Taxonomy state — loaded from published API, never from hard-coded maps
  const [taxonomy, setTaxonomy]     = useState(null);
  const [taxLoading, setTaxLoading] = useState(false);
  const [taxError, setTaxError]     = useState(null);

  // Edit-mode state
  const [searchQuery, setSearchQuery] = useState("");
  const [selCode, setSelCode]         = useState("");
  const [selState, setSelState]       = useState("");
  const [reason, setReason]           = useState("");
  const [err, setErr]                 = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [expandedTops, setExpandedTops] = useState(new Set());

  const load = useCallback(() => {
    Promise.all([
      api.get(`/documents-intel/${docId}/classification`),
      api.get("/documents-intel/categories"),
    ]).then(([cr, catr]) => {
      setRec(cr.data.record);
      setAiReady(cr.data.ai_ready);
      setCats(catr.data.categories || []);
    }).catch(() => {});
  }, [docId]);

  // Load published taxonomy — fetched once when editing begins, retried on explicit retry
  const loadTaxonomy = useCallback(() => {
    setTaxLoading(true);
    setTaxError(null);
    api.get("/document-taxonomy/published")
      .then((r) => {
        setTaxonomy(r.data);
        setTaxLoading(false);
      })
      .catch(() => {
        setTaxError("Could not load classification taxonomy.");
        setTaxLoading(false);
      });
  }, []);

  useEffect(() => { load(); }, [load]);

  // Fetch taxonomy when entering edit mode (only if not already loaded)
  useEffect(() => {
    if (mode === "editing" && !taxonomy && !taxLoading) {
      loadTaxonomy();
    }
  }, [mode, taxonomy, taxLoading, loadTaxonomy]);

  // Active classes from published taxonomy
  const activeClasses = useMemo(() => {
    if (!taxonomy?.classes) return [];
    return taxonomy.classes.filter((c) => c.active !== false);
  }, [taxonomy]);

  // Filtered classes based on search query
  const filteredClasses = useMemo(() => {
    const q = normalizeSearch(searchQuery);
    if (!q) return activeClasses;
    // Split the query into individual normalized words for multi-word AND-matching.
    // Single-word queries: substring match in spaced or dense haystack.
    // Multi-word queries ("Thai business", "1099 int"): every word must appear in haystack.
    const qWords = searchQuery.toLowerCase()
      .replace(/[-.,()]/g, "").trim().split(/\s+/).filter(Boolean).map(normalizeSearch);
    return activeClasses.filter((c) => {
      const parts = [
        normalizeSearch(c.label),
        normalizeSearch(c.code),
        ...(c.aliases || []).map(normalizeSearch),
        ...(c.searchable_terms || []).map(normalizeSearch),
        ...(c.ui_path || []).map(normalizeSearch),
      ];
      // Joined with space (for phrase matching) and without space (for compact form numbers)
      const haystackSpaced = parts.join(" ");
      const haystackDense  = parts.join("");
      // Full normalized query: matches "1099r" in "form1099r..."
      if (haystackSpaced.includes(q) || haystackDense.includes(q)) return true;
      // AND-match every word: handles "Thai business", "1099 retirement", "K-1 partnership"
      if (qWords.length > 1) {
        return qWords.every(
          (word) => haystackSpaced.includes(word) || haystackDense.includes(word)
        );
      }
      return false;
    });
  }, [activeClasses, searchQuery]);

  // Grouped hierarchy (used when not searching)
  const grouped = useMemo(() => buildHierarchy(filteredClasses), [filteredClasses]);

  // Find selected class object
  const selectedClass = useMemo(
    () => activeClasses.find((c) => c.code === selCode) || null,
    [activeClasses, selCode]
  );

  const suggest = async () => {
    setMode("suggesting"); setErr("");
    try {
      const r = await api.post(`/documents-intel/${docId}/classification/suggest`);
      setRec(r.data.record);
      setMode("idle");
      onRefresh?.();
    } catch (e) {
      setErr(e.response?.data?.detail?.message || e.response?.data?.message || "Suggestion failed.");
      setMode("idle");
    }
  };

  const confirmCode = async (code, jx) => {
    setMode("saving"); setErr("");
    try {
      const payload = { code, reason };
      if (STATE_CLASSES.has(code) && jx) payload.jurisdiction = jx;
      await api.post(`/documents-intel/${docId}/classification/confirm`, payload);
      try { await api.post(`/documents-intel/${docId}/requirement-matches/generate`); } catch { /* silent */ }
      setMode("idle"); setSearchQuery(""); setSelCode(""); setSelState(""); setReason("");
      load(); onRefresh?.();
    } catch (e) {
      setErr(e.response?.data?.detail?.message || e.response?.data?.detail || "Save failed.");
      setMode("idle");
    }
  };

  const save = () => {
    if (!selCode) { setErr("Please select a document type."); return; }
    if (STATE_CLASSES.has(selCode) && !selState) { setErr("Please select a state."); return; }
    confirmCode(selCode, selState);
  };

  const startEdit = () => {
    setMode("editing"); setSearchQuery(""); setSelCode(""); setSelState(""); setErr("");
    setExpandedTops(new Set(grouped.slice(0, 2).map((g) => g.topLabel)));
  };
  const cancelEdit = () => {
    setMode("idle"); setSearchQuery(""); setSelCode(""); setSelState(""); setErr("");
  };

  const toggleTop = (top) => {
    setExpandedTops((prev) => {
      const next = new Set(prev);
      next.has(top) ? next.delete(top) : next.add(top);
      return next;
    });
  };

  const isConfirmed = rec?.status === "confirmed";
  const isSuggested = rec?.status === "suggested" && rec?.suggested_class;
  const isStateClass = STATE_CLASSES.has(selCode);
  const isSearching  = searchQuery.trim().length > 0;
  const effectiveJurisdiction = (jurisdictions || []).map(jxName).filter(Boolean).join(", ") || null;
  const UPLOAD_KEYS = new Set(["client_upload", "staff_upload", "portal_upload"]);
  const displayCategory = (!UPLOAD_KEYS.has(category) && category && category !== "other") ? chip(category) : null;
  const displayType = (!UPLOAD_KEYS.has(documentType) && documentType) ? chip(documentType) : null;

  return (
    <div className="space-y-2 text-xs" data-testid="classification-panel">

      {/* ── Confirmed classification ── */}
      {isConfirmed && mode !== "editing" && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-2" data-testid="cls-confirmed">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <Icons.ShieldCheck className="h-3 w-3 text-emerald-600 shrink-0" />
              <span className="text-xs font-semibold text-emerald-800 truncate" data-testid="cls-canonical-label">
                {rec.canonical_label}
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => setShowDetails((p) => !p)}
                className="text-[10px] text-emerald-600 hover:text-emerald-800"
                data-testid="cls-details-toggle">
                {showDetails ? "Less" : "Details"}
              </button>
              <button onClick={startEdit}
                className="text-[10px] text-slate-400 hover:text-slate-600"
                data-testid="cls-change-btn">
                Change
              </button>
            </div>
          </div>
          <div className="text-[10px] text-emerald-600 mt-0.5 pl-[18px]" data-testid="cls-confirmed-by">
            Confirmed by {rec.confirmed_by}
          </div>
          {showDetails && (
            <div className="mt-1.5 pt-1.5 border-t border-emerald-200 space-y-0.5" data-testid="cls-details-expanded">
              {displayCategory && displayCategory !== chip(rec.canonical_label) && (
                <div className="text-[10px] text-emerald-700">Category: <span className="capitalize">{displayCategory}</span></div>
              )}
              {displayType && displayType !== chip(rec.canonical_label) && (
                <div className="text-[10px] text-emerald-700">Type: <span className="capitalize">{displayType}</span></div>
              )}
              {formNumber && <div className="text-[10px] text-emerald-700">Form: <span className="font-mono">{formNumber}</span></div>}
              {filingAuthority && <div className="text-[10px] text-emerald-700">Authority: {filingAuthority}</div>}
              {effectiveJurisdiction && <div className="text-[10px] text-emerald-700">Jurisdiction: {effectiveJurisdiction}</div>}
              {rec.overridden && rec.suggested_label && rec.suggested_label !== rec.canonical_label && (
                <div className="text-[10px] text-emerald-600">AI previously suggested: {rec.suggested_label}</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── AI suggestion awaiting staff decision ── */}
      {!isConfirmed && isSuggested && mode !== "editing" && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 space-y-1.5" data-testid="cls-suggested">
          <div className="flex items-center gap-1.5 font-semibold text-amber-800">
            <Icons.Sparkles className="h-3.5 w-3.5 shrink-0" />
            <span>Allison Suggestion</span>
            {rec.confidence_band && rec.confidence_band !== "unknown" && (
              <span className={`ml-auto px-1.5 py-0.5 rounded-full text-[9px] font-medium ${BAND_COLOR[rec.confidence_band] || BAND_COLOR.unknown}`}>
                {BAND_LABEL[rec.confidence_band] || chip(rec.confidence_band)} confidence
              </span>
            )}
          </div>
          <div className="text-amber-700 font-semibold text-[13px]" data-testid="cls-suggested-label">
            {rec.suggested_label}
          </div>
          {rec.confidence_reason && (
            <div className="text-[10px] text-amber-600">{rec.confidence_reason}</div>
          )}
          <div className="flex gap-1.5">
            <button onClick={() => confirmCode(rec.suggested_class, null)}
              disabled={mode === "saving"}
              className="flex-1 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded text-[10px] font-medium disabled:opacity-50"
              data-testid="cls-confirm-suggestion">
              {mode === "saving" ? "Saving…" : "Confirm"}
            </button>
            <button onClick={startEdit}
              className="flex-1 py-1 border border-amber-300 text-amber-700 rounded text-[10px] hover:bg-amber-100"
              data-testid="cls-override-suggestion">
              Override
            </button>
          </div>
        </div>
      )}

      {/* ── Unclassified — idle state ── */}
      {!isConfirmed && !isSuggested && mode === "idle" && (
        <div className="space-y-2" data-testid="cls-unclassified">
          <div className="flex items-center gap-1.5 text-slate-400 py-1">
            <Icons.Tag className="h-3.5 w-3.5" />
            <span>Unclassified</span>
          </div>
          {aiReady ? (
            <button onClick={suggest}
              className="w-full py-1.5 border border-blue-300 text-blue-700 rounded-lg text-[11px] hover:bg-blue-50 flex items-center justify-center gap-1.5"
              data-testid="cls-suggest-btn">
              <Icons.Sparkles className="h-3.5 w-3.5" />Ask Allison to Classify
            </button>
          ) : (
            <div className="text-[10px] text-slate-400 flex items-center gap-1">
              <Icons.Clock className="h-3 w-3" />AI classification available after text extraction
            </div>
          )}
          <button onClick={startEdit}
            className="w-full py-1.5 border border-slate-200 text-slate-600 rounded-lg text-[11px] hover:bg-slate-50"
            data-testid="cls-manual-btn">
            Classify Manually
          </button>
        </div>
      )}

      {/* ── Suggesting spinner ── */}
      {mode === "suggesting" && (
        <div className="flex items-center gap-2 text-slate-500 py-2" data-testid="cls-suggesting">
          <Icons.Loader2 className="h-4 w-4 animate-spin" />Running AI classification…
        </div>
      )}

      {/* ── Edit / manual classification form ── */}
      {(mode === "editing" || mode === "saving") && (
        <div className="space-y-2.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5" data-testid="cls-edit-form">
          <div className="font-semibold text-slate-700 text-[11px]">
            {isConfirmed ? "Change Classification" : "Select Classification"}
          </div>

          {/* Taxonomy loading / error */}
          {taxLoading && (
            <div className="flex items-center gap-2 text-slate-500 py-2" data-testid="cls-taxonomy-loading">
              <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span className="text-[11px]">Loading taxonomy…</span>
            </div>
          )}
          {taxError && !taxLoading && (
            <div className="rounded border border-rose-200 bg-rose-50 px-2 py-1.5 flex items-center justify-between gap-2"
              data-testid="cls-taxonomy-error">
              <span className="text-[10px] text-rose-700">{taxError}</span>
              <button onClick={loadTaxonomy}
                className="text-[10px] text-rose-600 underline hover:text-rose-800"
                data-testid="cls-taxonomy-retry">
                Retry
              </button>
            </div>
          )}

          {/* Search + browse — only shown when taxonomy is loaded */}
          {!taxLoading && !taxError && taxonomy && (
            <>
              {/* Search input */}
              <div className="relative">
                <Icons.Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400 pointer-events-none" />
                <input
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setSelCode(""); }}
                  placeholder="Search: 1099-R, PND90, gambling, Thai business…"
                  className="w-full text-[11px] border border-slate-200 rounded pl-6 pr-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                  data-testid="cls-search-input"
                  autoFocus
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="cls-search-clear">
                    <Icons.X className="h-3 w-3" />
                  </button>
                )}
              </div>

              {/* Class list — flat when searching, grouped when browsing */}
              <div className="max-h-48 overflow-y-auto rounded border border-slate-200 bg-white text-[11px]"
                data-testid="cls-class-browser">

                {/* Flat search results */}
                {isSearching && (
                  filteredClasses.length === 0 ? (
                    <div className="px-3 py-3 text-slate-400 text-center" data-testid="cls-search-empty">
                      No matching classifications
                    </div>
                  ) : (
                    filteredClasses.map((c) => (
                      <button key={c.code}
                        onClick={() => { setSelCode(c.code); setSelState(""); }}
                        className={`w-full text-left px-3 py-1.5 hover:bg-blue-50 border-b border-slate-100 last:border-0 ${
                          selCode === c.code ? "bg-blue-100 text-blue-800 font-medium" : "text-slate-700"
                        }`}
                        data-testid={`cls-option-${c.code}`}>
                        <div className="font-medium leading-tight">{c.label}</div>
                        {c.ui_path && c.ui_path.length > 0 && (
                          <div className="text-[9px] text-slate-400 mt-0.5 leading-tight">
                            {c.ui_path.join(" → ")}
                          </div>
                        )}
                      </button>
                    ))
                  )
                )}

                {/* Grouped browse */}
                {!isSearching && grouped.map((topGroup) => (
                  <div key={topGroup.topLabel}>
                    {/* Top-level group header */}
                    <button
                      onClick={() => toggleTop(topGroup.topLabel)}
                      className="w-full flex items-center justify-between px-3 py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-600 font-semibold border-b border-slate-200 text-[10px] uppercase tracking-wide"
                      data-testid={`cls-group-${topGroup.topLabel.replace(/\s+/g, "-").toLowerCase()}`}>
                      <span>{topGroup.topLabel}</span>
                      {expandedTops.has(topGroup.topLabel)
                        ? <Icons.ChevronDown className="h-3 w-3" />
                        : <Icons.ChevronRight className="h-3 w-3" />}
                    </button>

                    {/* Subgroups and classes */}
                    {expandedTops.has(topGroup.topLabel) && topGroup.subgroups.map((sub) => (
                      <div key={sub.subLabel || "_root"}>
                        {sub.subLabel && (
                          <div className="px-3 py-1 text-[10px] text-slate-500 font-medium bg-slate-50/50 border-b border-slate-100">
                            {sub.subLabel}
                          </div>
                        )}
                        {sub.classes.map((c) => (
                          <button key={c.code}
                            onClick={() => { setSelCode(c.code); setSelState(""); }}
                            className={`w-full text-left px-4 py-1.5 hover:bg-blue-50 border-b border-slate-100 last:border-0 ${
                              selCode === c.code ? "bg-blue-100 text-blue-800 font-medium" : "text-slate-700"
                            }`}
                            data-testid={`cls-option-${c.code}`}>
                            {c.label}
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              {/* Selected class display */}
              {selectedClass && (
                <div className="rounded border border-blue-200 bg-blue-50 px-2.5 py-1.5" data-testid="cls-selected-class">
                  <div className="text-[11px] font-semibold text-blue-800">{selectedClass.label}</div>
                  {selectedClass.ui_path && selectedClass.ui_path.length > 0 && (
                    <div className="text-[9px] text-blue-600 mt-0.5">{selectedClass.ui_path.join(" → ")}</div>
                  )}
                </div>
              )}

              {/* State selector — only for US state return classes */}
              {isStateClass && (
                <div>
                  <label className="text-[10px] text-slate-500 mb-0.5 block">
                    State <span className="text-rose-500">*</span>
                  </label>
                  <select
                    value={selState}
                    onChange={(e) => setSelState(e.target.value)}
                    className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                    data-testid="cls-state-select">
                    <option value="">— Select state —</option>
                    {US_STATE_OPTIONS.map((s) => (
                      <option key={s.code} value={s.code}>{s.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Reason */}
              <div>
                <label className="text-[10px] text-slate-500 mb-0.5 block">
                  Reason <span className="text-slate-300">(optional)</span>
                </label>
                <input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Corrected from W-2"
                  className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                  data-testid="cls-reason-input"
                />
              </div>
            </>
          )}

          {err && <div className="text-[10px] text-rose-600" data-testid="cls-error">{err}</div>}

          <div className="flex gap-1.5">
            <button
              onClick={save}
              disabled={!selCode || mode === "saving" || taxLoading || !!taxError}
              className="flex-1 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-[10px] font-medium disabled:opacity-50 flex items-center justify-center gap-1"
              data-testid="cls-save-btn">
              {mode === "saving" ? <><Icons.Loader2 className="h-3 w-3 animate-spin" />Saving…</> : "Confirm"}
            </button>
            <button
              onClick={cancelEdit}
              className="flex-1 py-1.5 border border-slate-200 text-slate-600 rounded text-[10px] hover:bg-slate-100"
              data-testid="cls-cancel-btn">
              Cancel
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
